import { Component, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FilterService, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { toGridData, toServerState } from '../common/grid-grouping';
import {
  cancelIcon,
  checkIcon,
  eyeIcon,
  fileClockIcon,
  gearIcon,
  undoIcon,
  userIcon,
  xIcon,
} from '@progress/kendo-svg-icons';
import { LoaderService } from '../loader/loader.service';
import { whitespace } from '../common/whitespace';
import {
  addTicketAttachments,
  ClinicOption,
  clinicLabel,
  filterClinicOptions,
  MAX_TICKET_ATTACHMENTS,
  MAX_TICKET_SUBJECT_LENGTH,
} from './admin-ticket';
import { ApiService } from '../service/api.service';
import {
  TICKET_LEGAL_NEXT_STATUSES,
  TicketPriority,
  TicketPriorityBadgeClass,
  TicketPriorityLabels,
  TicketStatus,
  TicketStatusBadgeClass,
  TicketStatusLabels,
} from '../common/enums/support-ticket.enum';
import {
  ApiEnvelope,
  SupportTicketDto,
  SupportTicketHistoryDto,
  SupportTicketMutationResponse,
  TicketService,
} from './ticket.service';
import { Observable } from 'rxjs';

interface FilterOption {
  Id: number;
  Name: string;
}

interface AssignableAgentOption {
  Id: number;
  fullname: string;
}

const toFilterOptions = (labels: Record<number, string>): FilterOption[] =>
  Object.entries(labels).map(([id, name]) => ({ Id: Number(id), Name: name }));

@Component({
  selector: 'app-tickets',
  templateUrl: './tickets.component.html',
  styleUrls: ['./tickets.component.css'],
  standalone: false
})
export class TicketsComponent implements OnInit {
  readonly icons = {
    view: eyeIcon,
    history: fileClockIcon,
    assign: userIcon,
    updateStatus: gearIcon,
    resolve: checkIcon,
    close: cancelIcon,
    reopen: undoIcon,
    remove: xIcon,
  };

  // Exposed so the template can compare against dataItem.Status without re-declaring the enum.
  readonly TicketStatus = TicketStatus;

  @ViewChild('viewTicketDialog') viewTicketDialog: TemplateRef<unknown>;
  @ViewChild('historyDialog') historyDialog: TemplateRef<unknown>;
  @ViewChild('assignDialog') assignDialog: TemplateRef<unknown>;
  @ViewChild('updateStatusDialog') updateStatusDialog: TemplateRef<unknown>;
  @ViewChild('resolveTicketDialog') resolveTicketDialog: TemplateRef<unknown>;
  @ViewChild('closeTicketDialog') closeTicketDialog: TemplateRef<unknown>;
  @ViewChild('reopenTicketDialog') reopenTicketDialog: TemplateRef<unknown>;
  @ViewChild('addTicketDialog') addTicketDialog: TemplateRef<unknown>;

  matDialogRef: MatDialogRef<unknown>;

  gridData: GridDataResult;
  kendoLoading = false;
  total = 0;
  gridHeight: number;
  searchString = '';
  // 0 = "All" sentinel, not null/undefined — Angular Material's mat-select treats a bound
  // null/undefined value as "clear the selection" (its ControlValueAccessor convention), so a
  // mat-option with [value]="null" can never actually show as selected. Real Status ids run
  // 1-7 and Priority ids 1-4, so 0 is a safe, unused sentinel for "All".
  selectedStatus = 0;
  selectedPriority = 0;

  public state: State = {
    skip: 0,
    take: 10,
    sort: [{ field: 'CreatedOn', dir: 'desc' }],
    filter: { logic: 'and', filters: [] },
    group: []
  };

  public pageableWithAll: object = {
    buttonCount: 5,
    pageSizes: [5, 10, 20, 50, 100, { text: 'All', value: 'all' }]
  };

  statusOptions: FilterOption[] = [{ Id: 0, Name: 'All Statuses' }];
  priorityOptions: FilterOption[] = [{ Id: 0, Name: 'All Priorities' }];
  // Choices of the Status / Priority column filters - the API stores both as numeric ids, so they are picked by name
  readonly statusFilterOptions = toFilterOptions(TicketStatusLabels);
  readonly priorityFilterOptions = toFilterOptions(TicketPriorityLabels);

  // Add Ticket - an admin raising a ticket on a clinic's behalf (work item 27050)
  readonly clinicLabel = clinicLabel;
  readonly maxTicketAttachments = MAX_TICKET_ATTACHMENTS;
  readonly maxTicketSubjectLength = MAX_TICKET_SUBJECT_LENGTH;
  addTicketForm!: FormGroup;
  clinicOptions: ClinicOption[] = [];
  filteredClinicOptions: ClinicOption[] = [];
  ticketAttachments: File[] = [];
  assignableAgents: AssignableAgentOption[] = [];
  updateStatusOptions: { value: TicketStatus; label: string }[] = [];

  isAdmin = false;
  currentUserId: number | null = null;

  selectedTicket: SupportTicketDto | null = null;
  historyData: SupportTicketHistoryDto[] = [];
  historyLoading = false;

  assignForm!: FormGroup;
  statusForm!: FormGroup;
  resolveForm!: FormGroup;
  closeForm!: FormGroup;
  reopenForm!: FormGroup;

  actionSaving = false;
  actionError: string | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly loaderService: LoaderService,
    private readonly toaster: ToastrService,
    private readonly ticketService: TicketService,
    private readonly apiService: ApiService,
  ) { }

  ngOnInit(): void {
    this.readCurrentUser();
    this.buildForms();
    this.setGridHeight();
    this.loadTicketMeta();
    this.loadAssignableAgents();
    this.loadTickets();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.setGridHeight();
  }

  private setGridHeight(): void {
    const headerHeight = 70;
    const footerHeight = 61;
    const otherElementsHeight = 125;
    this.gridHeight = window.innerHeight - headerHeight - footerHeight - otherElementsHeight;
  }

  private readCurrentUser(): void {
    const loginData = JSON.parse(localStorage['loginData'] ?? '{}');
    this.isAdmin = loginData?.IsAdmin === true;
    this.currentUserId = loginData?.Id ?? null;
  }

  private buildForms(): void {
    this.assignForm = this.formBuilder.group({
      AssignedToUserId: [null, Validators.required]
    });
    this.statusForm = this.formBuilder.group({
      Status: [null, Validators.required],
      ResolutionNotes: ['']
    });
    this.resolveForm = this.formBuilder.group({
      ResolutionNotes: ['', Validators.required]
    });
    this.closeForm = this.formBuilder.group({
      ClosingNote: ['']
    });
    this.reopenForm = this.formBuilder.group({
      Reason: ['']
    });
    this.addTicketForm = this.formBuilder.group({
      // The picked ClinicOption; while the admin is still typing to search, the typed text
      Clinic: [null, TicketsComponent.clinicPicked],
      Subject: ['', [Validators.required, whitespace, Validators.maxLength(MAX_TICKET_SUBJECT_LENGTH)]],
      Priority: [TicketPriority.Normal, Validators.required],
      Description: ['', [Validators.required, whitespace]]
    });
  }

  /** The Clinic field is only valid once a clinic has been picked from the list, not just typed. */
  private static clinicPicked(control: AbstractControl): ValidationErrors | null {
    return control.value && typeof control.value === 'object' ? null : { clinicRequired: true };
  }

  // ---------- Grid load / filters ----------

  loadTickets(): void {
    this.loaderService.show();
    this.kendoLoading = true;
    this.ticketService.getAllTickets(
      toServerState(this.state),
      this.searchString.trim(),
      this.selectedStatus || null,
      this.selectedPriority || null
    ).subscribe({
      next: (res) => {
        if (res?.Success && res?.Data) {
          this.gridData = toGridData(res.Data, res.Total ?? 0, this.state);
          this.total = res.Total ?? 0;
        } else {
          this.gridData = { data: [], total: 0 };
          this.total = 0;
        }
        this.kendoLoading = false;
        this.loaderService.hide();
      },
      error: (_err) => {
        this.gridData = { data: [], total: 0 };
        this.total = 0;
        this.kendoLoading = false;
        this.loaderService.hide();
        this.toaster.error('Something went wrong. Unable to load support tickets.');
      }
    });
  }

  dataStateChange(state: State): void {
    this.state = state;
    this.loadTickets();
  }

  filterBySearch(value: string): void {
    this.searchString = value;
    this.state = { ...this.state, skip: 0 };
    this.loadTickets();
  }

  clearSearch(): void {
    if (!this.searchString) return;
    this.searchString = '';
    this.state = { ...this.state, skip: 0 };
    this.loadTickets();
  }

  onStatusFilterChange(value: number): void {
    this.selectedStatus = value;
    this.state = { ...this.state, skip: 0 };
    this.loadTickets();
  }

  onPriorityFilterChange(value: number): void {
    this.selectedPriority = value;
    this.state = { ...this.state, skip: 0 };
    this.loadTickets();
  }

  private loadTicketMeta(): void {
    this.ticketService.getTicketMeta().subscribe({
      next: (res) => {
        if (res?.Success && res?.Data) {
          this.statusOptions = [{ Id: 0, Name: 'All Statuses' }, ...(res.Data.StatusList ?? [])];
          this.priorityOptions = [{ Id: 0, Name: 'All Priorities' }, ...(res.Data.PriorityList ?? [])];
        }
      },
      // Keep the default "All" placeholders; grid badges still render fine from the local enum
      // labels regardless of whether this meta call succeeds.
      error: (_err) => undefined
    });
  }

  private loadAssignableAgents(): void {
    this.apiService.getAllUsers().subscribe({
      next: (response: any) => {
        if (response?.Success === true && response?.Data != null) {
          this.assignableAgents = (response.Data as any[])
            .filter(item => item.IsActive === true || item.IsActive === 'True')
            .map(item => ({
              Id: item.Id,
              fullname: `${item.FirstName} ${item.LastName}`
            }));
        }
      },
      // The Assign dialog simply shows an empty agent list if this call fails; the grid itself
      // doesn't depend on it.
      error: (_err) => undefined
    });
  }

  // ---------- Badge helpers ----------

  getStatusBadgeClass(status: number): string {
    return TicketStatusBadgeClass[status as TicketStatus] ?? 'badge-secondary';
  }

  getPriorityBadgeClass(priority: number): string {
    return TicketPriorityBadgeClass[priority as TicketPriority] ?? 'badge-secondary';
  }

  /** Priority and Status group on their numeric ids - group headers show the matching name instead. */
  optionName(options: FilterOption[], id: number): string {
    return options.find(option => option.Id === id)?.Name ?? String(id);
  }

  /** The id a Status / Priority column filter currently matches, if any. */
  optionFilterValue(filter: CompositeFilterDescriptor, field: string): number | undefined {
    const match = filter?.filters.find(child => (child as FilterDescriptor).field === field) as FilterDescriptor | undefined;
    return match?.value;
  }

  /** Sets a Status / Priority column filter to the picked option - an exact match on its id. */
  filterByOption(filterService: FilterService, field: string, id: number): void {
    filterService.filter({ logic: 'and', filters: [{ field, operator: 'eq', value: id }] });
  }

  /** `AttachmentImageUrls` is a JSON array string on the wire (e.g. '["https://...","https://..."]')
   *  or null/empty when no images were attached — parse it defensively so a malformed value never
   *  breaks the view dialog. */
  getAttachmentUrls(ticket: SupportTicketDto | null): string[] {
    if (!ticket?.AttachmentImageUrls) return [];
    try {
      const parsed = JSON.parse(ticket.AttachmentImageUrls);
      return Array.isArray(parsed) ? parsed.filter((url): url is string => typeof url === 'string') : [];
    } catch {
      return [];
    }
  }

  // ---------- Permission / transition helpers ----------
  // Mirrors PlusDoctor.Services.SupportTicketService exactly (AssignAsync's assignableFrom list,
  // UpdateStatusAsync's IsLegalWorkTransition, CloseAsync/ReopenAsync's own status guards) so the
  // UI never offers an action the backend will reject — see SupportTicketService.cs.

  canAssign(ticket: SupportTicketDto): boolean {
    if (!this.isAdmin) return false;
    return ticket.Status !== TicketStatus.Resolved && ticket.Status !== TicketStatus.Closed;
  }

  legalNextStatuses(currentStatus: number): TicketStatus[] {
    return TICKET_LEGAL_NEXT_STATUSES[currentStatus as TicketStatus] ?? [];
  }

  canUpdateStatus(ticket: SupportTicketDto): boolean {
    if (this.legalNextStatuses(ticket.Status).length === 0) return false;
    if (this.isAdmin) return true;
    return ticket.AssignedToUserId === this.currentUserId;
  }

  canResolve(ticket: SupportTicketDto): boolean {
    return this.canUpdateStatus(ticket) && this.legalNextStatuses(ticket.Status).includes(TicketStatus.Resolved);
  }

  canClose(ticket: SupportTicketDto): boolean {
    return this.isAdmin && ticket.Status === TicketStatus.Resolved;
  }

  canReopen(ticket: SupportTicketDto): boolean {
    return this.isAdmin && (ticket.Status === TicketStatus.Resolved || ticket.Status === TicketStatus.Closed);
  }

  // ---------- Dialog open/close ----------

  closeDialog(): void {
    this.dialog?.closeAll();
    this.actionError = null;
  }

  openViewDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.loaderService.show();
    this.ticketService.getById(ticket.Id).subscribe({
      next: (res) => {
        this.loaderService.hide();
        if (res?.Success && res?.Data) {
          this.selectedTicket = res.Data;
        }
        this.matDialogRef = this.dialog.open(this.viewTicketDialog, { disableClose: true, autoFocus: false });
      },
      error: (_err) => {
        this.loaderService.hide();
        // Fall back to the row data already on screen rather than blocking the dialog entirely.
        this.matDialogRef = this.dialog.open(this.viewTicketDialog, { disableClose: true, autoFocus: false });
      }
    });
  }

  openHistoryDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.historyData = [];
    this.historyLoading = true;
    this.matDialogRef = this.dialog.open(this.historyDialog, { disableClose: true, autoFocus: false });
    this.ticketService.getHistory(ticket.Id).subscribe({
      next: (res) => {
        this.historyLoading = false;
        this.historyData = (res?.Success && res?.Data) ? res.Data : [];
      },
      error: (_err) => {
        this.historyLoading = false;
        this.historyData = [];
        this.toaster.error('Unable to load ticket history.');
      }
    });
  }

  openAssignDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.actionError = null;
    this.assignForm.reset({ AssignedToUserId: ticket.AssignedToUserId ?? null });
    this.matDialogRef = this.dialog.open(this.assignDialog, { disableClose: true, autoFocus: false });
  }

  openUpdateStatusDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.actionError = null;
    this.updateStatusOptions = this.legalNextStatuses(ticket.Status)
      .map(value => ({ value, label: TicketStatusLabels[value] }));
    this.statusForm.reset({ Status: null, ResolutionNotes: '' });
    this.statusForm.get('ResolutionNotes')?.clearValidators();
    this.statusForm.get('ResolutionNotes')?.updateValueAndValidity();
    this.matDialogRef = this.dialog.open(this.updateStatusDialog, { disableClose: true, autoFocus: false });
  }

  onStatusChoiceChange(value: TicketStatus): void {
    const notesControl = this.statusForm.get('ResolutionNotes');
    if (value === TicketStatus.Resolved) {
      notesControl?.setValidators([Validators.required]);
    } else {
      notesControl?.clearValidators();
    }
    notesControl?.updateValueAndValidity();
  }

  openResolveDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.actionError = null;
    this.resolveForm.reset({ ResolutionNotes: '' });
    this.matDialogRef = this.dialog.open(this.resolveTicketDialog, { disableClose: true, autoFocus: false });
  }

  openCloseDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.actionError = null;
    this.closeForm.reset({ ClosingNote: '' });
    this.matDialogRef = this.dialog.open(this.closeTicketDialog, { disableClose: true, autoFocus: false });
  }

  openAddTicketDialog(): void {
    this.actionError = null;
    this.ticketAttachments = [];
    this.addTicketForm.reset({ Clinic: null, Subject: '', Priority: TicketPriority.Normal, Description: '' });
    this.filteredClinicOptions = filterClinicOptions(this.clinicOptions, '');
    if (!this.clinicOptions.length) {
      this.loadClinicOptions();
    }
    this.matDialogRef = this.dialog.open(this.addTicketDialog, { disableClose: true, autoFocus: false, width: '640px' });
  }

  private loadClinicOptions(): void {
    this.ticketService.getClinicOptions().subscribe({
      next: (res) => {
        this.clinicOptions = res?.Success && res.Data ? res.Data : [];
        this.filteredClinicOptions = filterClinicOptions(this.clinicOptions, this.clinicSearchText());
      },
      error: () => this.toaster.error('Unable to load the clinic list.')
    });
  }

  /** What the admin has typed in the Clinic field, or the picked clinic's name. */
  private clinicSearchText(): string {
    const value = this.addTicketForm.get('Clinic')?.value;
    return typeof value === 'string' ? value : clinicLabel(value);
  }

  onClinicSearch(text: string): void {
    this.filteredClinicOptions = filterClinicOptions(this.clinicOptions, text);
  }

  onAttachmentsPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const result = addTicketAttachments(this.ticketAttachments, Array.from(input.files ?? []));
    this.ticketAttachments = result.files;
    if (result.skipped > 0) {
      this.toaster.warning(`Only image files can be attached, up to ${MAX_TICKET_ATTACHMENTS} per ticket.`);
    }
    // Let the same file be picked again after removing it
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.ticketAttachments = this.ticketAttachments.filter((_, i) => i !== index);
  }

  openReopenDialog(ticket: SupportTicketDto): void {
    this.selectedTicket = ticket;
    this.actionError = null;
    this.reopenForm.reset({ Reason: '' });
    this.matDialogRef = this.dialog.open(this.reopenTicketDialog, { disableClose: true, autoFocus: false });
  }

  // ---------- Submit handlers ----------

  submitAssign(): void {
    if (this.assignForm.invalid || !this.selectedTicket) {
      this.assignForm.markAllAsTouched();
      return;
    }
    if (this.actionSaving) return;
    const ticketId = this.selectedTicket.Id;
    const assignedToUserId = this.assignForm.value.AssignedToUserId;
    this.runMutation(this.ticketService.assign(ticketId, assignedToUserId), 'Ticket assigned successfully.');
  }

  submitUpdateStatus(): void {
    if (this.statusForm.invalid || !this.selectedTicket) {
      this.statusForm.markAllAsTouched();
      return;
    }
    if (this.actionSaving) return;
    const { Status, ResolutionNotes } = this.statusForm.value;
    this.runMutation(
      this.ticketService.updateStatus(this.selectedTicket.Id, Status, ResolutionNotes),
      'Ticket status updated successfully.'
    );
  }

  submitResolve(): void {
    if (this.resolveForm.invalid || !this.selectedTicket) {
      this.resolveForm.markAllAsTouched();
      return;
    }
    if (this.actionSaving) return;
    this.runMutation(
      this.ticketService.updateStatus(this.selectedTicket.Id, TicketStatus.Resolved, this.resolveForm.value.ResolutionNotes),
      'Ticket resolved successfully.'
    );
  }

  submitClose(): void {
    if (!this.selectedTicket || this.actionSaving) return;
    this.runMutation(
      this.ticketService.close(this.selectedTicket.Id, this.closeForm.value.ClosingNote),
      'Ticket closed successfully.'
    );
  }

  submitAddTicket(): void {
    if (this.addTicketForm.invalid) {
      this.addTicketForm.markAllAsTouched();
      return;
    }
    if (this.actionSaving) return;
    const { Clinic, Subject, Priority, Description } = this.addTicketForm.value;
    this.runMutation(
      this.ticketService.adminCreate({
        companyId: (Clinic as ClinicOption).Id,
        subject: Subject,
        description: Description,
        priority: Priority,
        attachmentImages: this.ticketAttachments,
      }),
      'Ticket raised successfully.'
    );
  }

  submitReopen(): void {
    if (!this.selectedTicket || this.actionSaving) return;
    this.runMutation(
      this.ticketService.reopen(this.selectedTicket.Id, this.reopenForm.value.Reason),
      'Ticket reopened successfully.'
    );
  }

  private runMutation(obs: Observable<SupportTicketMutationResponse>, successMessage: string): void {
    this.actionSaving = true;
    this.actionError = null;
    this.loaderService.show();
    obs.subscribe({
      next: (response: ApiEnvelope<unknown>) => {
        this.actionSaving = false;
        this.loaderService.hide();
        if (!response?.Success) {
          this.actionError = response?.Message ?? 'Something went wrong. Please try again.';
          this.toaster.error(this.actionError);
          return;
        }
        this.toaster.success(successMessage);
        this.closeDialog();
        this.loadTickets();
      },
      error: (err: any) => {
        this.actionSaving = false;
        this.loaderService.hide();
        this.actionError = err?.error?.Message ?? 'Something went wrong. Please try again.';
        this.toaster.error(this.actionError);
      }
    });
  }
}
